'use client';

import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { Loader2 } from 'lucide-react';

import { cn } from '@/lib/utils';

/** Matches {@link CreatorGridToolsPanel.EditPanelChrome} header rows; green for protocol collect / Add yield lane. */
const TONE = {
  emerald: {
    border: 'border-emerald-500/30',
    icon: 'text-emerald-200',
    label: 'text-emerald-100',
    trailing: 'text-emerald-200/85',
    hoverBg: 'hover:bg-white/[0.03]',
  },
  /** Same chrome as creator `Edit Panel` — creator-only yield actions on adapter cards. */
  amber: {
    border: 'border-amber-500/30',
    icon: 'text-amber-200',
    label: 'text-amber-100',
    trailing: 'text-amber-200/80',
    hoverBg: 'hover:bg-white/[0.03]',
  },
} as const;

export type LobbyYieldPanelActionTone = keyof typeof TONE;

/** Full-width mono row used under Board Yield cards (parity with collapsible Edit Panel strips). */
export function LobbyYieldPanelActionButton({
  tone,
  icon: Icon,
  label,
  trailing,
  disabled,
  pending,
  onClick,
  type = 'button',
  className,
  labelClassName,
  'aria-label': ariaLabel,
}: {
  tone: LobbyYieldPanelActionTone;
  icon: LucideIcon;
  label: ReactNode;
  trailing?: ReactNode;
  disabled?: boolean;
  pending?: boolean;
  onClick?: () => void;
  type?: 'button' | 'submit';
  className?: string;
  /** Merged onto the label span (e.g. `normal-case whitespace-nowrap` for longer copy). */
  labelClassName?: string;
  'aria-label'?: string;
}) {
  const c = TONE[tone];
  const inactive = !!disabled || !!pending;
  const noTrailing = trailing == null || trailing === '';

  return (
    <button
      type={type}
      aria-label={ariaLabel}
      disabled={inactive}
      onClick={onClick}
      className={cn(
        'w-full flex min-w-0 flex-nowrap items-center gap-2 px-2.5 py-1.5 text-left rounded-[6px]',
        'bg-black/50 backdrop-blur-md border transition-colors',
        inactive ? 'opacity-[0.42] saturate-[0.75] cursor-not-allowed' : [c.hoverBg, 'cursor-pointer'],
        c.border,
        className,
      )}
    >
      {pending ? (
        <Loader2 className={cn('h-3 w-3 shrink-0 animate-spin', c.icon)} aria-hidden />
      ) : (
        <Icon className={cn('h-3 w-3 shrink-0', c.icon)} aria-hidden />
      )}
      <span
        className={cn(
          'text-[10px] font-mono min-w-0',
          c.label,
          labelClassName
            ? labelClassName
            : cn('tracking-[0.2em] uppercase', noTrailing && 'flex-1 truncate'),
        )}
      >
        {label}
      </span>
      {trailing != null && trailing !== '' ? (
        <span className={cn('text-[10px] font-mono tracking-[0.12em] tabular-nums ml-auto truncate', c.trailing)}>
          {trailing}
        </span>
      ) : null}
    </button>
  );
}
